// Icon factory (009 T031, research W-17): each icon is redrawn from the SVGs of the Figma reference export as a
// component that strokes or fills with `currentColor`, is hidden from assistive technology (the control carries the
// name) and, when directional, mirrors in right-to-left layouts.
import type { ReactNode, SVGProps } from 'react';

/** Props of every icon: size in CSS pixels plus any SVG attribute. */
export type IconProps = Omit<SVGProps<SVGSVGElement>, 'children'> & { size?: number };

/** An icon component. */
export type Icon = ((props: IconProps) => ReactNode) & { displayName?: string };

/**
 * Creates an icon component.
 * @param name the component's display name
 * @param viewBox the drawing's view box
 * @param body the SVG shapes
 * @param options `directional` mirrors the icon in right-to-left layouts; `fill` fills instead of stroking
 * @returns the icon component
 */
export function createIcon(
  name: string,
  viewBox: string,
  body: ReactNode,
  options: { directional?: boolean; fill?: boolean } = {},
): Icon {
  const Component = ({ size = 20, className = '', ...rest }: IconProps) => (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill={options.fill ? 'currentColor' : 'none'}
      stroke={options.fill ? 'none' : 'currentColor'}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={
        `${options.directional ? 'rtl:-scale-x-100 ' : ''}${className}`.trim() || undefined
      }
      {...rest}
    >
      {body}
    </svg>
  );
  Component.displayName = name;
  return Component;
}
